import React, { useEffect, useMemo, useRef, useState } from 'react';
import { User } from 'firebase/auth';
import { ArtrackTracksApi, StorageApi, Configuration as SdkConfiguration } from '../../sdk';
import { sdkStorageProvider } from '../../services/storageProvider';
import { getAuthIdToken } from '../../services/authToken';
import type { StorageObjectResponse } from '../../sdk';

type TileSource = 'recent' | 'cloud' | 'public' | 'track' | 'created';

interface QuickOpenOverlayProps {
    show: boolean;
    onClose: () => void;
    onLoadPresentation: (content: string, filename: string) => void;
    user?: User | null;
    onOpenEditor?: (content: string, filename: string) => void;
    onImportTrack?: (trackId: number, options?: { useUTM?: boolean; utmZone?: string; altitudeScale?: number; trackScale?: number }) => Promise<void> | void;
}

interface QuickOpenTileData {
    id: string;
    title: string;
    subtitle?: string;
    source: TileSource;
    content?: string;
    fileName?: string;
    coverUrl?: string;
    storageId?: number;
    fileUrl?: string;
    isPublic?: boolean;
}

function extractTitleFromMarkdown(markdown: string): string {
    const m = markdown.match(/^#\s+(.+)$/m);
    if (m && m[1]) return m[1].trim();
    const line = markdown.split(/\r?\n/).find(l => l.trim().length > 0);
    return line ? line.replace(/^#+\s*/, '').trim().substring(0, 80) : 'Untitled';
}

function extractFirstImageFromMarkdown(markdown: string): string | undefined {
    const m = markdown.match(/!\[[^\]]*\]\(([^)]+)\)/);
    if (m && m[1]) return m[1];
    return undefined;
}

function getLastOpenedKey(uid?: string | null): string {
    const id = uid || 'guest';
    return `3dp:last-opened:${id}`;
}

// --- Styles ---
const btnStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.1)', color: 'white',
    border: '1px solid rgba(255,255,255,0.25)', padding: '10px 12px', borderRadius: 10,
    cursor: 'pointer', fontSize: 13
};
const smallBtnStyle: React.CSSProperties = {
    ...btnStyle, padding: '4px 8px', borderRadius: 6, fontSize: 11
};

export const QuickOpenOverlay: React.FC<QuickOpenOverlayProps> = ({ show, onClose, onLoadPresentation, user, onOpenEditor, onImportTrack }) => {
    const [tiles, setTiles] = useState<QuickOpenTileData[]>([]);
    const [query, setQuery] = useState('');
    const [isLoadingCloud, setIsLoadingCloud] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [trackIdInput, setTrackIdInput] = useState('');
    const [showAIModal, setShowAIModal] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<'mine' | 'public'>('mine');

    async function uploadAndRewriteImages(markdown: string): Promise<string> {
        // Server-side media ingestion is a privileged mutation. Keep external
        // URLs unchanged until a user-authenticated backend-for-frontend exists.
        return markdown;
    }

    // Refresh tile list
    const refreshTiles = async () => {
        if (!show) return;
        setError(null);
        const list: QuickOpenTileData[] = [];

        // Last opened from localStorage
        try {
            const last = localStorage.getItem(getLastOpenedKey(user?.uid));
            if (last) {
                const parsed = JSON.parse(last) as { filename: string; content: string; ts: number };
                const title = extractTitleFromMarkdown(parsed.content) || 'Untitled';
                const coverUrl = extractFirstImageFromMarkdown(parsed.content);
                list.push({
                    id: `recent:${parsed.filename}:${parsed.ts}`,
                    title,
                    subtitle: 'Recent',
                    source: 'recent',
                    content: parsed.content,
                    coverUrl
                });
            }
        } catch {}

        setTiles(list);

        // Public reads are keyless. Private libraries and all management actions
        // stay disabled until Storage accepts a real user identity.
        setIsLoadingCloud(true);
        try {
            // Public presentations
            try {
                const pubObjects = await sdkStorageProvider.listPublicPresentationObjects();
                const pubTiles: QuickOpenTileData[] = pubObjects
                    .map((it: StorageObjectResponse) => ({
                        id: `public:${it.id}`,
                        title: it.original_filename?.replace(/\.(md|markdown)$/i, '') || `#${it.id}`,
                        subtitle: 'Public',
                        source: 'public' as const,
                        coverUrl: undefined, // markdown files have no image thumbnail
                        storageId: it.id,
                        fileUrl: it.file_url,
                        isPublic: true
                    }));
                setTiles(prev => [...prev, ...pubTiles]);
            } catch {}

            // ARTracks
            try {
                const SDK_BASE_URL = (import.meta as any).env?.VITE_SDK_BASE_URL || 'https://api-storage.arkturian.com';
                const idToken = await getAuthIdToken();
                const tracksApi = new ArtrackTracksApi(new SdkConfiguration({
                    basePath: SDK_BASE_URL,
                    baseOptions: { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
                }));
                const tracksRes = await tracksApi.listTracksArtrackTracksGet('public', 24, 0, undefined);
                const tracks = (tracksRes?.data as any)?.tracks || (Array.isArray(tracksRes?.data) ? tracksRes.data : []);
                if (Array.isArray(tracks) && tracks.length > 0) {
                    setTiles(prev => [
                        ...prev,
                        ...tracks.map((t: any) => ({
                            id: `track:${t.id}`,
                            title: t.name || `Track #${t.id}`,
                            subtitle: 'Track',
                            source: 'track' as const,
                            coverUrl: t.thumbnailUrl || t.coverUrl || undefined,
                            content: `# ${t.name || 'Track'}\n\nImported from ARTrack (ID: ${t.id}).`
                        }))
                    ]);
                }
            } catch {}
        } catch (e: any) {
            console.warn('Failed to load presentations:', e?.message || e);
        } finally {
            setIsLoadingCloud(false);
        }
    };

    useEffect(() => { refreshTiles(); }, [show, user?.uid]);

    // Filtered tiles by search and section
    const filtered = useMemo(() => {
        let result = tiles;
        // Section filter
        if (activeSection === 'mine') {
            result = result.filter(t => t.source === 'recent' || t.source === 'cloud' || t.source === 'track');
        } else {
            result = result.filter(t => t.source === 'public');
        }
        // Text search
        const q = query.trim().toLowerCase();
        if (q) result = result.filter(t => t.title.toLowerCase().includes(q));
        return result;
    }, [tiles, query, activeSection]);

    const handleOpenFilePicker = () => fileInputRef.current?.click();

    const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = file.name.toLowerCase().split('.').pop();
        if (ext !== 'md' && ext !== 'markdown') { setError('Please select a Markdown (.md) file'); return; }
        const text = await file.text();
        onLoadPresentation(text, file.name);
        onClose();
    };

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text || text.trim().length === 0) { setError('Clipboard is empty'); return; }
            const isMarkdown = /(^#\s)|(!\[.*\]\(.*\))|(```)/m.test(text);
            if (!isMarkdown) { setError('Clipboard does not look like Markdown'); return; }
            onLoadPresentation(text, 'Pasted.md');
            onClose();
        } catch { setError('Unable to read from clipboard'); }
    };

    const handleCreateNew = () => {
        const markdown = `---GLOBAL---\nlayouter: grid\ngridSpacing: 4\n---\n\n# New Presentation\n\nFirst slide content...\n\n---\n\n# Slide 2\n\nMore content here...\n`;
        if (onOpenEditor) {
            onOpenEditor(markdown, 'New_Presentation.md');
        } else {
            onLoadPresentation(markdown, 'New_Presentation.md');
        }
        onClose();
    };

    const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
        e.preventDefault(); e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        const ext = file.name.toLowerCase().split('.').pop();
        if (ext !== 'md' && ext !== 'markdown') { setError('Drop a Markdown (.md) file'); return; }
        const text = await file.text();
        onLoadPresentation(text, file.name);
        onClose();
    };

    const openTile = async (tile: QuickOpenTileData, opts?: { openAndEdit?: boolean }) => {
        try {
            let content = tile.content;
            let filename = tile.fileName || `${tile.title}.md`;

            // If no content cached, fetch from SDK
            if (!content && tile.storageId) {
                const loaded = await sdkStorageProvider.loadPresentationByObjectId(tile.storageId);
                content = loaded.content;
                filename = loaded.filename;
            } else if (!content && tile.fileUrl) {
                content = await sdkStorageProvider.loadPresentationByFileUrl(tile.fileUrl);
            }

            if (!content) { setError('Could not load presentation content'); return; }

            if (opts?.openAndEdit && onOpenEditor) {
                onOpenEditor(content, filename);
            } else {
                onLoadPresentation(content, filename);
            }
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Failed to open presentation');
        }
    };

    const handleDeleteTile = async (tile: QuickOpenTileData, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile.storageId) return;
        if (!confirm(`Delete "${tile.title}"?`)) return;
        try {
            await sdkStorageProvider.deletePresentationObject(tile.storageId);
            setTiles(prev => prev.filter(t => t.id !== tile.id));
        } catch (err: any) {
            setError(err?.message || 'Failed to delete');
        }
    };

    const handleTogglePublic = async (tile: QuickOpenTileData, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile.storageId) return;
        const newPublic = !tile.isPublic;
        try {
            await sdkStorageProvider.togglePublic(tile.storageId, newPublic);
            setTiles(prev => prev.map(t => t.id === tile.id ? { ...t, isPublic: newPublic, subtitle: newPublic ? 'Public' : 'Private' } : t));
        } catch (err: any) {
            setError(err?.message || 'Failed to update visibility');
        }
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = async (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
        if (!filtered.length) return;
        const cols = 4;
        if (e.key === 'ArrowRight') { e.preventDefault(); setFocusedIndex(i => Math.min(filtered.length - 1, i + 1)); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); setFocusedIndex(i => Math.max(0, i - 1)); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedIndex(i => Math.min(filtered.length - 1, i + cols)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedIndex(i => Math.max(0, i - cols)); }
        else if (e.key === 'Enter') { e.preventDefault(); await openTile(filtered[focusedIndex], { openAndEdit: e.shiftKey }); }
    };

    const handleImportTrack = async () => {
        const id = parseInt(trackIdInput, 10);
        if (!id || !onImportTrack) { setError('Enter a valid track ID'); return; }
        try { await onImportTrack(id); onClose(); } catch (e: any) { setError(e?.message || 'Failed to import track'); }
    };

    const handleGenerateWithAI = async () => {
        setAiError(null);
        if (!aiPrompt.trim()) { setAiError('Please enter a short description.'); return; }
        try {
            setAiLoading(true);
            const AI_API_BASE = (import.meta as any).env?.VITE_AI_API_BASE || 'https://api-ai.arkturian.com';
            const formatPreamble = `You generate a 3D Presenter markdown document. The output MUST start with a ---GLOBAL--- block that defines shared label settings, followed by slides separated by "---".

Output format:

---GLOBAL---
layouter: line
showLabel: true
labelRenderMode: CANVAS
labelDistance: 0.3
labelLatitude: -55
labelColor: #e0e0e0
labelBackgroundColor: #000000cc
labelBorderRadius: 8
---

<!--
shape: tile
color: #2a2a2a
depth: 0.05
scale: 2.0
uri: https://commons.wikimedia.org/wiki/Special:FilePath/FILENAME.jpg
-->
# Slide Title
Short paragraphs of content.

Rules:
- ALWAYS start with ---GLOBAL--- block containing ALL label properties (showLabel, labelRenderMode, labelDistance, labelLatitude, labelColor, labelBackgroundColor, labelBorderRadius)
- labelRenderMode MUST be CANVAS (not html)
- Do NOT repeat label properties in individual slides — they inherit from GLOBAL
- Individual slides only contain: shape, color, depth, scale, opacity, cornerRadius, uri
- 4-8 slides, concise readable text
- For images, ONLY use Wikimedia Commons: https://commons.wikimedia.org/wiki/Special:FilePath/REAL_FILENAME.jpg
- Do NOT use Unsplash, Pexels, or any other source
- Do NOT invent filenames — only use files you are certain exist on Wikimedia Commons
- If unsure about an image, omit the uri line entirely
- uri must be a simple string (not an array)
- You may vary color/scale/opacity per slide for visual variety`;

            const geminiUrl = `${AI_API_BASE.replace(/\/$/, '')}/ai/gemini`;
            const response = await fetch(geminiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: `${formatPreamble}\nUser request:\n${aiPrompt}`, max_tokens: 4000 })
            });
            if (!response.ok) throw new Error(`AI API returned ${response.status}`);
            const payload = await response.json();
            let markdown = payload?.response || payload?.message || payload?.text || '';
            if (!markdown || typeof markdown !== 'string') throw new Error('AI did not return markdown');
            markdown = await uploadAndRewriteImages(markdown);
            const aiTitle = `ai_${aiPrompt.trim().replace(/[^a-zA-Z0-9äöüÄÖÜß]+/g, '_').substring(0, 40)}`;
            onLoadPresentation(markdown, `${aiTitle}.md`);
            setShowAIModal(false);
            onClose();
        } catch (e: any) {
            setAiError(e?.message || 'AI generation failed');
        } finally {
            setAiLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}
            style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            tabIndex={0} onKeyDown={handleKeyDown}
        >
            {/* Close button */}
            <div style={{ position: 'absolute', top: 24, right: 24 }}>
                <button onClick={onClose} style={{ ...btnStyle, padding: '8px 12px', borderRadius: 8 }}>X</button>
            </div>

            <div style={{ maxWidth: 1100, margin: '60px auto 40px', padding: '0 20px' }}>
                {/* Search + Actions — wraps on mobile */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                    <input value={query} onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search presentations..."
                        style={{ flex: '1 1 200px', minWidth: 0, background: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, padding: '10px 12px', fontSize: 16 }}
                    />
                    <button onClick={handleCreateNew} style={{ ...btnStyle, color: '#90ee90', flexShrink: 0 }}>+ New</button>
                    <button onClick={handleOpenFilePicker} style={{ ...btnStyle, flexShrink: 0 }}>Import</button>
                    <button onClick={handlePaste} style={{ ...btnStyle, flexShrink: 0 }}>Paste</button>
                    <button onClick={() => setShowAIModal(true)} style={{ ...btnStyle, color: '#90ee90', flexShrink: 0 }}>AI Generate</button>
                    <input ref={fileInputRef} type="file" accept=".md,.markdown" style={{ display: 'none' }} onChange={handleFileChange} />
                </div>

                {/* Section tabs: Mine / Public — wraps on mobile */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                    <button onClick={() => setActiveSection('mine')}
                        style={{ ...btnStyle, padding: '8px 16px', flexShrink: 0, background: activeSection === 'mine' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.05)', fontWeight: activeSection === 'mine' ? 700 : 400 }}>
                        My Presentations
                    </button>
                    <button onClick={() => setActiveSection('public')}
                        style={{ ...btnStyle, padding: '8px 16px', flexShrink: 0, background: activeSection === 'public' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.05)', fontWeight: activeSection === 'public' ? 700 : 400 }}>
                        Public
                    </button>
                    {/* Track import */}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ color: 'white', opacity: 0.6, fontSize: 12 }}>Track:</span>
                        <input value={trackIdInput} onChange={(e) => setTrackIdInput(e.target.value)}
                            placeholder="ID" inputMode="numeric"
                            style={{ width: 70, background: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '6px 8px', fontSize: 16 }}
                        />
                        <button onClick={handleImportTrack} style={{ ...smallBtnStyle }}>Go</button>
                    </div>
                </div>

                {error && <div style={{ color: '#ff8a80', marginBottom: 12 }}>{error}</div>}

                {/* Tile grid — 1-2 cols on mobile, more on desktop */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))', gap: 12 }}>
                    {filtered.map((tile, index) => (
                        <div role="button" key={tile.id} onClick={() => openTile(tile)}
                            onContextMenu={(e) => { e.preventDefault(); openTile(tile, { openAndEdit: true }); }}
                            tabIndex={-1} aria-selected={index === focusedIndex}
                            style={{
                                display: 'block', textAlign: 'left', background: 'rgba(255,255,255,0.08)',
                                border: index === focusedIndex ? '2px solid #66ccff' : '1px solid rgba(255,255,255,0.25)',
                                borderRadius: 12, overflow: 'hidden', color: 'white', position: 'relative'
                            }}>
                            {/* Cover */}
                            <div style={{ width: '100%', aspectRatio: '16 / 10', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {tile.coverUrl ? (
                                    <img src={tile.coverUrl} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ fontSize: 56, opacity: 0.7 }}>{(tile.title || 'U').charAt(0).toUpperCase()}</div>
                                )}
                            </div>
                            {/* Info + Actions */}
                            <div style={{ padding: '10px 12px' }}>
                                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>{tile.title}</span>
                                    {tile.storageId && <span style={{ fontSize: 10, opacity: 0.4, fontWeight: 400 }}>#{tile.storageId}</span>}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.75, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <span style={{
                                        padding: '1px 6px', borderRadius: 4, fontSize: 10,
                                        background: tile.isPublic ? 'rgba(144,238,144,0.2)' : 'rgba(255,255,255,0.1)',
                                        color: tile.isPublic ? '#90ee90' : 'rgba(255,255,255,0.6)'
                                    }}>{tile.subtitle || tile.source}</span>
                                    {tile.source !== 'public' && (
                                        <span onClick={(e) => { e.stopPropagation(); openTile(tile, { openAndEdit: true }); }}
                                            style={{ color: '#aee1ff', fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }}>Edit</span>
                                    )}
                                    {/* Actions for own tiles */}
                                    {user && tile.source === 'cloud' && tile.storageId && (
                                        <>
                                            <span onClick={(e) => handleTogglePublic(tile, e)}
                                                style={{ cursor: 'pointer', fontSize: 11, color: tile.isPublic ? '#90ee90' : 'rgba(255,255,255,0.5)' }}
                                                title={tile.isPublic ? 'Make private' : 'Make public'}>
                                                {tile.isPublic ? 'Public' : 'Private'}
                                            </span>
                                            <span onClick={(e) => handleDeleteTile(tile, e)}
                                                style={{ cursor: 'pointer', fontSize: 11, color: '#ff8a80' }}
                                                title="Delete">Del</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {isLoadingCloud && Array.from({ length: 4 }).map((_, i) => (
                        <div key={`skeleton-${i}`} style={{ height: 180, borderRadius: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    ))}
                </div>

                {!isLoadingCloud && filtered.length === 0 && (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px 0' }}>
                        {activeSection === 'public' ? 'No public presentations found' : 'No presentations yet. Create one or import a markdown file.'}
                    </div>
                )}
            </div>

            {/* AI Modal */}
            {showAIModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
                    <div style={{ background: 'rgba(30,30,30,0.95)', color: 'white', padding: 20, borderRadius: 12, width: 520, maxWidth: '90vw', border: '1px solid rgba(255,255,255,0.2)' }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>Generate with AI</div>
                        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>Describe the presentation you want. We'll generate 4-12 slides.</div>
                        <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={6}
                            placeholder="e.g., A product tour with 6 slides: overview, features, demo, pricing, testimonials, CTA."
                            style={{ width: '100%', background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: 10, resize: 'vertical', fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                        {aiError && <div style={{ color: '#ff8a80', marginTop: 8 }}>{aiError}</div>}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            <button onClick={() => setShowAIModal(false)} disabled={aiLoading} style={btnStyle}>Cancel</button>
                            <button onClick={handleGenerateWithAI} disabled={aiLoading} style={{ ...btnStyle, background: '#1e90ff' }}>
                                {aiLoading ? 'Generating...' : 'Generate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickOpenOverlay;
